import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useLeaderboardFilters, filterInfluencer, type LeaderboardFilters, type ColumnFilter } from "@/components/LeaderboardFilters";
import { useLeaderboard, type LeaderboardEntry } from "@/hooks/useLeaderboard";

const STORAGE_KEY = "global-leaderboard-filters";

interface GlobalInfluencerFilterContextValue {
  filters: LeaderboardFilters;
  updateFilter: (column: keyof LeaderboardFilters, bound: "min" | "max", value: string) => void;
  clearFilters: () => void;
  hasAnyFilter: boolean;
  /** Set of x_user_ids that pass the global filter. Empty set = no filter active (show all). */
  qualifyingIds: Set<string>;
  /** Count of influencers that pass the filter (when active). */
  qualifyingCount: number;
  /** Total influencer count. */
  totalCount: number;
}

const Ctx = createContext<GlobalInfluencerFilterContextValue | null>(null);

export function GlobalInfluencerFilterProvider({ children }: { children: ReactNode }) {
  const { filters, updateFilter, clearFilters, hasAnyFilter } = useLeaderboardFilters(STORAGE_KEY);
  const { data: influencers = [] } = useLeaderboard();

  const { qualifyingIds, qualifyingCount } = useMemo(() => {
    if (!hasAnyFilter) return { qualifyingIds: new Set<string>(), qualifyingCount: influencers.length };
    const ids = new Set<string>();
    for (const inf of influencers) {
      if (filterInfluencer(inf, filters)) {
        ids.add(inf.id);
      }
    }
    return { qualifyingIds: ids, qualifyingCount: ids.size };
  }, [influencers, filters, hasAnyFilter]);

  const value: GlobalInfluencerFilterContextValue = {
    filters,
    updateFilter,
    clearFilters,
    hasAnyFilter,
    qualifyingIds,
    qualifyingCount,
    totalCount: influencers.length,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useGlobalInfluencerFilter() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useGlobalInfluencerFilter must be used within GlobalInfluencerFilterProvider");
  return ctx;
}
